import { IFormApplicationDTO, ChatMessage } from 'shared/interfaces/formApplication';
import { InternalUser } from 'shared/interfaces/form';

export interface ChatParticipant {
    name: string;
    isApplicant: boolean;
    isManager: boolean;
    lastMessageTime?: Date;
}

/**
 * Extract all unique participants from the application's approval flow
 */
export function getChatParticipants(application: IFormApplicationDTO, userManager?: InternalUser | null): ChatParticipant[] {
    const participants = new Set<string>();
    const participantInfo: { [name: string]: ChatParticipant } = {};

    // Add applicant
    if (application.applicantName) {
        participants.add(application.applicantName);
        participantInfo[application.applicantName] = {
            name: application.applicantName,
            isApplicant: true,
            isManager: false,
        };
    }

    // Add manager if exists
    if (userManager?.name) {
        participants.add(userManager.name);
        participantInfo[userManager.name] = {
            name: userManager.name,
            isApplicant: false,
            isManager: true,
        };
    }

    // Extract participants from approval flow (#143: the external view only carries the
    // decider's display name — approver groups and their members are not exposed).
    application.approvalFlow.forEach((stage) => {
        stage.decisions?.forEach((decision) => {
            if (decision.decisionUserName) {
                participants.add(decision.decisionUserName);
                if (!participantInfo[decision.decisionUserName]) {
                    participantInfo[decision.decisionUserName] = {
                        name: decision.decisionUserName,
                        isApplicant: false,
                        isManager: decision.approverType === 'Manager',
                    };
                }
            }
        });
    });

    return Array.from(participants).map((name) => participantInfo[name]);
}

/**
 * Get available chats for the current user
 */
export function getAvailableChats(
    application: IFormApplicationDTO,
    currentUser: string,
    userManager?: InternalUser | null
): ChatParticipant[] {
    const allParticipants = getChatParticipants(application, userManager);

    // Filter out the current user from the list
    return allParticipants.filter((participant) => participant.name !== currentUser);
}

/**
 * Filter messages for a specific chat
 */
export function filterMessagesForChat(
    messages: ChatMessage[],
    currentUser: string,
    selectedChat: 'public' | string
): ChatMessage[] {
    if (selectedChat === 'public') {
        // Show only public messages (including legacy messages without the 'public' field)
        return messages.filter((msg) => msg.public === true || msg.public === undefined);
    }

    // Private chat - show messages between current user and selected participant
    return messages.filter((msg) => {
        // For backward compatibility, treat messages without 'public' field as public
        if (msg.public === undefined || msg.public === true) {
            return false; // Don't show public messages in private chats
        }

        return (
            msg.public === false &&
            ((msg.author === currentUser && msg.to === selectedChat) || (msg.author === selectedChat && msg.to === currentUser))
        );
    });
}

/**
 * Sort participants by priority and last message time
 */
export function sortChatParticipants(
    participants: ChatParticipant[],
    messages: ChatMessage[],
    currentUser: string
): ChatParticipant[] {
    // Calculate last message time for each participant
    const participantsWithLastMessage = participants.map((participant) => {
        const lastMessage = messages
            .filter(
                (msg) =>
                    msg.public === false &&
                    ((msg.author === currentUser && msg.to === participant.name) ||
                        (msg.author === participant.name && msg.to === currentUser))
            )
            .sort((a, b) => new Date(b.messageTimestamp).getTime() - new Date(a.messageTimestamp).getTime())[0];

        return {
            ...participant,
            lastMessageTime: lastMessage?.messageTimestamp,
        };
    });

    // Sort by priority: applicant first, then manager, then by last message time
    return participantsWithLastMessage.sort((a, b) => {
        // Applicant comes first
        if (a.isApplicant && !b.isApplicant) return -1;
        if (!a.isApplicant && b.isApplicant) return 1;

        // Manager comes second
        if (a.isManager && !b.isManager) return -1;
        if (!a.isManager && b.isManager) return 1;

        // Then sort by last message time (most recent first)
        if (a.lastMessageTime && b.lastMessageTime) {
            return new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime();
        }
        if (a.lastMessageTime && !b.lastMessageTime) return -1;
        if (!a.lastMessageTime && b.lastMessageTime) return 1;

        // Finally, sort alphabetically
        return a.name.localeCompare(b.name);
    });
}
